import { CBM, CONDITIONAL, LOGIC, MATH, DBM } from "Pages/GMS/ISR/ISRConstants";
import { SearchIcon, XIcon } from "assests/GmsIcons";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  TConnector,
  TField,
  TIndexState,
  TProductCategory,
  TSubmodel,
  TUpsertISRProjectDataModelsInput,
} from "../model";
import CBMRule from "../../Rules/CBMRule";
import MathRuleComponent from "Pages/GMS/ISR/Components/Rules/MathRuleComponent";
import ConditionalRuleComponent from "Pages/GMS/ISR/Components/Rules/ConditionalRuleComponent";
import LogicRuleComponent from "Pages/GMS/ISR/Components/Rules/LogicRuleComponent";
import DBMRule from "Pages/GMS/ISR/Components/Rules/DBMRule";
import Search from "Pages/GMS/Search";
import SidePanelComponent from "Pages/GMS/ISR/Components/RuleComponents/SidePanelComponent";
import { SB3RuleContext } from "Pages/GMS/ISR/Components/Rules/SB3RuleContext";
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Button from "Components/DesignSystem/Button/Button";
import Typography from "Components/DesignSystem/Typography/Typography";

type TManageRulesDialogProps = {
  showRulesMenu: boolean;
  setShowRulesMenu: React.Dispatch<React.SetStateAction<boolean>>;
  selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
  setUserProjectDataModels: React.Dispatch<
    React.SetStateAction<TUpsertISRProjectDataModelsInput>
  >;
  userProjectDataModels: TUpsertISRProjectDataModelsInput;
  indexInfo?: TIndexState;
  editId?: string|undefined|null;
  ruleType?: string|undefined|null;
  createISRProject:(isRuleUpdate: boolean)=>void;
  featureType: string
  /** DBM saves to its own `isr_rules` document, not the project — lets the list refetch. */
  onDbmRuleSaved?: () => void;
};


const ManageRulesDialog = ({
  showRulesMenu,
  setShowRulesMenu,
  selectedItem,
  userProjectDataModels,
  setUserProjectDataModels,
  indexInfo,
  editId,
  ruleType,
  createISRProject,
  featureType,
  onDbmRuleSaved
}: TManageRulesDialogProps) => {
  const { updateModelData, showPickPanel, handleSaveRule, logicRule, handleUpdateRule, resetLogicRule } = useContext(SB3RuleContext)
  const conditionalSaveRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const cbmSaveRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    updateModelData(userProjectDataModels?.project)
  }, [userProjectDataModels])

  const toggleMenu = () => {
    setShowRulesMenu(!showRulesMenu);
  };

  // DBM is a self-contained DS SidePanel (its own badge, width, save/close
  // header actions) rather than a slot inside this component's hand-rolled
  // overlay/drawer chrome — render it alone, not wrapped in the markup below.
  if (ruleType === DBM) {
    return (
      <DBMRule
        selectedItem={selectedItem}
        userProjectDataModels={userProjectDataModels}
        indexInfo={indexInfo}
        editId={editId}
        ruleType={ruleType}
        toggleMenu={toggleMenu}
        onSaved={onDbmRuleSaved}
      />
    );
  }

  /* The DS scrim (motion.ts): bg-opacity on grey-900 rather than element
     opacity, plus the backdrop blur every DS Dialog/SidePanel already wears. */
  const overlayClasses =
    "tw-absolute tw-inset-0 tw-bg-grey-900 tw-bg-opacity-50 tw-backdrop-blur-sm tw-z-40 tw-h-full tw-w-full";
  const menuClasses = showRulesMenu
    ? `tw-absolute tw-top-0 tw-right-0 tw-h-full tw-max-h-full tw-overflow-auto ${ruleType === 'Logic' && showPickPanel ? "tw-w-[84%]" : "tw-w-[65%]"}  tw-bg-surface tw-z-50  tw-shadow-lg`
    : "tw-hidden";

  const handleSaveOrUpdate = () => {
    if (logicRule?.name) {
      if (logicRule?.rule_type === LOGIC.toLocaleLowerCase() && logicRule?.id) {
        handleUpdateRule()
      } else {
        handleSaveRule(userProjectDataModels?.project?.id)
    }
    }
    else toast.info("Rule name is mandatory")
  }

  return (
    <div className="tw-h-[calc(100vh-60px)] tw-fixed tw-top-0 tw-left-0 tw-w-full tw-z-40 tw-mt-16 ">
      <div data-testid="components-managerulesdialog-div-1" className={overlayClasses} onClick={() => toggleMenu()} />
      <div className={menuClasses}>
        <div
          className={`tw-flex tw-justify-between tw-items-center tw-border-b tw-border-edge tw-pb-2 tw-pt-4 tw-pr-2 tw-pl-2 tw-sticky tw-top-0 tw-bg-surface tw-z-10`}
        >
          {/* Typography variants are SIZE tokens — without an explicit ink the
              h2 inherits the ambient Bootstrap near-black and vanishes on the
              dark surface (the ISRHomePage h1 bug, same day). */}
          <Typography variant="title-header" as="h2" className="tw-text-ink">
            {ruleType} Rule
          </Typography>
          <div className="tw-flex tw-items-center tw-gap-5">
            <Button
              variant="secondary"
              size="sm"
              data-testid="components-managerulesdialog-button-1"
              onClick={() => { resetLogicRule(); toggleMenu() }}
            >
              Close
            </Button>
            {ruleType === CONDITIONAL ? <Button
              variant="primary"
              size="sm"
              data-testid="components-managerulesdialog-button-2"
              onClick={() => conditionalSaveRef.current?.()}
            >
              Save
            </Button> : null}
            {ruleType === CBM ? <Button
              variant="primary"
              size="sm"
              data-testid="components-managerulesdialog-button-3"
              onClick={() => cbmSaveRef.current?.()}
            >
              Save
            </Button> : null}
            {ruleType === LOGIC ? <Button
              variant="primary"
              size="sm"
              data-testid="components-managerulesdialog-button-4"
              onClick={() => { return ruleType === LOGIC ? handleSaveOrUpdate() : toggleMenu() }}
            >
              {logicRule?.id ? "Update" : "Done"}
            </Button> : null}
          </div>
        </div>
        {/* <RulesAddEditManage ruleId={'0'} rule_version={'Live'} /> */}
        <div>
          {ruleType === MATH && (
            <MathRuleComponent
              selectedItem={selectedItem}
              userProjectDataModels={userProjectDataModels}
              setUserProjectDataModels={setUserProjectDataModels}
              indexInfo={indexInfo}
              editId={editId}
              ruleType={ruleType}
              toggleMenu={toggleMenu}
              createISRProject={createISRProject}
              featureType={featureType}
            />
          )}
          {ruleType === CBM && (
            <CBMRule
              selectedItem={selectedItem}
              userProjectDataModels={userProjectDataModels}
              setUserProjectDataModels={setUserProjectDataModels}
              indexInfo={indexInfo}
              editId={editId}
              ruleType={ruleType}
              toggleMenu={toggleMenu}
              createISRProject={createISRProject}
              saveRef={cbmSaveRef}
            />
          )}
          {ruleType === CONDITIONAL && (
            <ConditionalRuleComponent
              selectedItem={selectedItem}
              userProjectDataModels={userProjectDataModels}
              setUserProjectDataModels={setUserProjectDataModels}
              indexInfo={indexInfo}
              editId={editId}
              ruleType={ruleType}
              toggleMenu={toggleMenu}
              createISRProject={createISRProject}
              featureType={featureType}
              saveRef={conditionalSaveRef}
          />
          )}

          {ruleType === LOGIC && (
            <div className="tw-flex  tw-bg-surface-sunken tw-border tw-border-edge">
              {showPickPanel &&
                <SidePanelComponent />
              }

              <LogicRuleComponent />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ManageRulesDialog;
